import React, { useState, useEffect } from 'react';
import { AppShellSkeleton } from '@/components/layout/AppShellSkeleton';
import { LoadingScreen } from '@/components/loading/LoadingScreen';
import { StoreHydrationGate } from './StoreHydrationGate';
import { LazyImportWrapper } from './LazyImportWrapper';
import { bootStateMachine } from '@/lib/boot/BootStateMachine';
import { PRODUCTION_CONFIG } from '@/config/production';
import MaintenancePage from '@/pages/MaintenancePage';

/**
 * Bootstrap component that renders skeleton immediately,
 * then lazy-loads the main app after store hydration
 */
export const Bootstrap: React.FC = () => {
  const [shouldLoadApp, setShouldLoadApp] = useState(false);

  // Log bootstrap start with timing and initialize boot state machine
  React.useEffect(() => {
    console.log('[BOOT] bootstrap_start', {
      step: 'bootstrap_start',
      ts: Date.now(),
      safeMode: isSafeMode()
    });

    // Early STORAGE_OK transition - prevents race condition with StoreHydrationGate
    if (bootStateMachine.canTransition('STORAGE_OK')) {
      bootStateMachine.transition('STORAGE_OK');
    }
  }, []);

  // Check for safe mode
  const isSafeMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const safeParam = urlParams.get('safe');
    const safeModeStorage = localStorage?.getItem('emergency_safe_mode');
    
    return safeParam === '1' || safeModeStorage === 'true';
  };

  useEffect(() => {
    if (isSafeMode()) {
      console.log('Safe mode detected, rendering minimal UI');
      return;
    }

    // Defer app loading to next tick to ensure skeleton renders first
    const timer = setTimeout(() => {
      console.log('✅ [BOOT_STEP_OK]', {
        step: 'app_loading_triggered',
        ts: Date.now(),
        safeMode: false
      });
      setShouldLoadApp(true);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  // Maintenance mode (project pause) - render maintenance page immediately
  // Bypass with ?maintenance=0 or ?safe=1 for admin access
  if (PRODUCTION_CONFIG.isMaintenanceMode()) {
    return <MaintenancePage />;
  }

  // Safe mode UI
  if (isSafeMode()) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <h1 style={{ marginBottom: '16px', fontSize: '24px' }}>Safe Mode</h1>
        <p style={{ marginBottom: '20px', textAlign: 'center' }}>
          The application is running in safe mode to help recover from issues.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              localStorage?.removeItem('emergency_safe_mode');
              window.location.href = window.location.pathname;
            }}
            style={{
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Exit Safe Mode
          </button>
          <button
            onClick={() => {
              localStorage?.clear();
              window.location.reload();
            }}
            style={{
              padding: '12px 24px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Clear All Data & Restart
          </button>
        </div>
      </div>
    );
  }

  // Show enhanced loading screen while app loads
  if (!shouldLoadApp) {
    return <LoadingScreen message="Initializing application..." showProgress={true} />;
  }

  // Lazy load the main app
  return (
    <LazyImportWrapper
      importFn={() => import('@/AppContentNew')}
      fallback={<LoadingScreen message="Loading workspace..." />}
      onError={async (error) => {
        console.error('Failed to load main AppContent component:', error);
        try {
          const { productionLogger } = await import('@/modules/production-audit');
          productionLogger.error('system', 'AppContent component failed to load', { error: error.message });
        } catch (logError) {
          console.error('Failed to log error:', logError);
        }
      }}
    >
      {(AppContentComponent) => (
        <StoreHydrationGate>
          <AppContentComponent />
        </StoreHydrationGate>
      )}
    </LazyImportWrapper>
  );
};